<template>
    <div class="payment-chart-table">
        <button class="btn btn-blue amortization-table-btn" @click="viewAmortizationTable()">{{ !ShowAmortizationTable ? "View" : "Close" }} Amortization Table</button>
        <div v-show="ShowAmortizationTable" class="table-responsive">
            <table v-for="(yearData, year) in amortizationBreakdown" :key="year" class="table" :class="{active: IsActive[year]}">
                <thead>
                    <tr>
                        <th width="16%">
                            <label class="table-trigger" @click="toggleActive(year)">
                                <span class="lt4-ChevronRight"></span> {{ year }}
                            </label>
                        </th>
                        <th width="14%">
                            Principal:
                            <big>${{ currencyFilter(yearData.totalPrincipal) }}</big>
                        </th>
                        <th width="14%">
                            Interest:
                            <big>${{ currencyFilter(yearData.totalInterest) }}</big>
                        </th>
                        <th width="14%">
                            Taxes & Fees:
                            <lt-label :tooltip-link="TaxesFeesTooltip" :unique-id="year" />
                            <big>${{ currencyFilter(yearData.totalTaxesFees) }}</big>
                        </th>
                        <th v-if="extraPayment > 0 || cashBomb > 0" width="14%">
                            Extra Payments:
                            <big>${{ currencyFilter(yearData.totalExtraPayments) }}</big>
                        </th>
                        <th width="14%">
                            Total Payment:
                            <big>${{ currencyFilter(yearData.totalPrincipal + yearData.totalInterest + yearData.totalExtraPayments + yearData.totalTaxesFees) }}</big>
                        </th>
                        <th width="14%">
                            Loan Balance:
                            <big>${{ currencyFilter(yearData.totalLoanBalance) }}</big>
                        </th>
                    </tr>
                </thead>
                <tbody class="active">
                    <tr v-for="(monthData, monthIndex) in yearData.monthsDetail" :key="monthIndex">
                        <td>{{ monthData.namedMonth }}</td>
                        <td>${{ currencyFilter(monthData.principal) }}</td>
                        <td>${{ currencyFilter(monthData.interestPaid) }}</td>
                        <td>${{ currencyFilter(monthData.otherExpenses) }}</td>
                        <td v-if="extraPayment > 0 || cashBomb > 0">${{ currencyFilter(monthData.extraPayment) }}</td>
                        <td>${{ currencyFilter(monthData.principal + monthData.interestPaid + monthData.extraPayment + monthData.otherExpenses) }}</td>
                        <td>${{ currencyFilter(monthData.balance) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>
<script>
    import Accounting from 'accounting';
    import AmortizationBreakdownHelper from '~/helpers/mortgage-amortization-breakdown';
    import DefaultTooltip from '~/constants/tooltip-defaults';
    import LtLabel from '~/components/lt-label.vue';

    export default {
        props: {
            totalPrincipal: {
                default: '969.60',
            },
            totalInterest: {
                default: '1682.97',
            },
            totalTaxesFees: {
                default: '3800.00',
            },
            totalExtraPayments: {
                default: '4201.72',
            },
            totalPayment: {
                default: '3782.58',
            },
            totalLoanBalance: {
                default: '198707.79',
            },
            loanAmount: {
                default: 0,
            },
            loanTermInYears: {
                default: 0,
            },
            interestRate: {
                default: 0,
            },
            showOtherExpenses: {
                default: 1,
            },
            showTotalPayment: {
                default: 1,
            },
            additionalMonthlyExpenses: {
                default: 0,
            },
            homeValue: {
                default: 0,
            },
            extraPayment: {
                default: 0,
            },
            includePmi: {
                default: 0,
            },
            taxesFeesTooltip: {
                default: DefaultTooltip.TaxesFees,
            },
            showAmortizationTable: {
                default: true,
            },
            startDate: {
                default: '',
            },
            paymentFrequency: {
                default: 'monthly',
            },
            cashBombFrequency: {
                default: '',
            },
            cashBomb: {
                default: 0,
            },
            cashBombStartDate: {
                default: '',
            },
        },
        data() {
            return {
                IsActive: {
                    2017: false,
                },
                ShowAmortizationTable: this.showAmortizationTable,
                TaxesFeesTooltip: parseInt(this.hideTooltip, 10) ? '' : this.taxesFeesTooltip,
            };
        },
        computed: {
            CashBombStartDate() {
                return new Date(this.cashBombStartDate);
            },
            CashBombAmount() {
                return this.cashBomb;
            },
            CashBombFrequencyInMonths() {
                return this.cashBombFrequency === 'one-time'
                    ? 1
                    : this.cashBombFrequency === 'quarterly'
                        ? 3
                        : 12;
            },
            PaymentFrequency() {
                const lastDate = new Date(this.startDate);
                lastDate.setMonth(lastDate.getMonth() + this.loanTermInYears * 12 - 1);
                return this.paymentFrequency === 'monthly'
                    ? 12
                    : this.paymentFrequency === 'bi-weekly'
                        ? Math.round((lastDate.getTime() - this.startDate.getTime()) / 86400000 / 14 / this.loanTermInYears)
                        : Math.round((lastDate.getTime() - this.startDate.getTime()) / 86400000 / 7 / this.loanTermInYears);
            },
            amortizationBreakdown() {
                const data = AmortizationBreakdownHelper.amortizationBreakdown(this.loanAmount, this.loanTermInYears, this.interestRate, this.homeValue, this.additionalMonthlyExpenses, this.extraPayment, this.includePmi, this.startDate ? this.startDate.toString() : '', this.PaymentFrequency, this.CashBombAmount, this.CashBombFrequencyInMonths, this.CashBombStartDate);

                const dataWithoutExtraPay = AmortizationBreakdownHelper.amortizationBreakdown(this.loanAmount, this.loanTermInYears, this.interestRate, this.homeValue, this.additionalMonthlyExpenses, 0, this.includePmi, this.startDate ? this.startDate.toString() : '', 12, 0, this.CashBombFrequencyInMonths, this.CashBombStartDate);

                this.$emit('UpdateTableCalculation', { loanAllYearDetail: data }, { detailWithoutExtraPay: dataWithoutExtraPay });
                return data.amortizationsByYear;
            },
        },
        methods: {
            toggleActive(year) {
                this.IsActive[year] = !this.IsActive[year];
            },
            currencyFilter(figure) {
                return Accounting.formatNumber(figure, { precision: 2 });
            },
            viewAmortizationTable() {
                this.ShowAmortizationTable = !this.ShowAmortizationTable;
            },
            getToggle() {
                const d = new Date(this.startDate);
                for (let i = d.getFullYear(); i < d.getFullYear() + 200; ++i) {
                    this.$set(this.IsActive, i, false);
                }
            },
        },
        mounted() {
            this.getToggle();
        },
        watch: {
            startDate() {
                this.getToggle();
            },
        },
        components: {
            LtLabel,
        },
        mixins: [Accounting],
    };
</script>
<style lang="scss" src="../../../assets/styles/amortization-breakdown-table.scss"></style>
